import { NgClass } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';

import * as _ from 'underscore';

import { CasinoMyBetsIntegratedService } from '../../../betHistory/services/CasinoMyBetsIntegratedService/casino-mybets-integrated.service';
import { GtmService } from '../../../core/services/gtm/gtm.service';
import { GtmTrackingService } from '../../../core/services/gtmTracking/gtm-tracking.service';
import { LocaleService } from '../../../core/services/locale/locale.service';
import { NavigationService } from '../../../core/services/navigation/navigation.service';
import { StorageService } from '../../../core/services/storage/storage.service';
import { ScrollableDirective } from '../../directives/scrollable.directive';
import { ITab } from './tabs-panel.model';

@Component({
    selector: 'tabs-panel',
    templateUrl: 'tabs-panel.component.html',
    styleUrls: ['tabs-panel.component.scss'],
    imports: [ScrollableDirective, NgClass],
})
export class TabsPanelComponent implements OnChanges, OnDestroy, OnInit, AfterViewInit {
    @Output() readonly tpFunction?: EventEmitter<{ id: string; tab: ITab }> = new EventEmitter();

    @Input() tpTabs: ITab[];
    @Input() routerLinkDisable: boolean;
    @Input() tpActiveTab?: ITab;
    @Input() tbClass?: boolean;
    @Input() typeId?: string | null;
    @Input() tpFuncArr?: string;
    @Input() hidden?: boolean;
    @Input() detectGTMLocation?: string;
    @Input() maxElementsToDisplay?: number;
    @Input() preBuilt?: string;

    setActiveTabForCasino: boolean = false;

    constructor(
        public elementRef: ElementRef,
        public locale: LocaleService,
        public router: Router,
        public gtmTrackingService: GtmTrackingService,
        public casinoMyBetsIntegratedService: CasinoMyBetsIntegratedService,
        protected navigationService: NavigationService,
        protected storage: StorageService,
        protected gtmService: GtmService,
    ) {}

    /**
     * Tab Function
     * @param {ITab} tab
     * @param {MouseEvent} $event
     */
    clickFunction(tab: ITab, $event: MouseEvent): void {
        $event.preventDefault();

        this.detectActiveTab(tab);
        if (tab.url && !this.routerLinkDisable) {
            if (this.tpActiveTab?.name && tab?.name) {
                this.navigationService.openUrl(tab.url, true, true, null, '', true, {
                    previousTab: `${this.tpActiveTab.name.toLowerCase()} bets`,
                    currentTab: `${tab.name.toLowerCase()}`,
                });
            } else {
                this.navigationService.openUrl(tab.url, true, true);
            }
        }

        if (this.tpFunction?.observers?.length) {
            this.tpFunction.emit({ id: tab?.[this.tpFuncArr as keyof ITab], tab: tab });
        }
        /* For surfacebet reactions GA Track */
        this.storage.remove('activepill');

        this.trackGTMData(tab);
    }

    /**
     * track active tab swith GTM
     * return void
     */
    trackGTMData(tab: ITab): void {
        const gtmData = {
            'event': 'Event.Tracking',
            'component.CategoryEvent': 'betslip',
            'component.LabelEvent': 'my bets',
            'component.ActionEvent': 'click',
            'component.PositionEvent': this.tpActiveTab?.title || 'Open',
            'component.LocationEvent': 'my bets',
            'component.EventDetails': tab.title,
            'component.URLClicked': window.location.hostname + tab.url,
        };
        this.gtmService.push(gtmData.event, gtmData);
    }

    /**
     * Check for 5ASide Tab
     * @param tab
     * @returns {boolean}
     */
    is5ASideTab(tab: ITab): boolean {
        return tab.marketName === '5-a-side';
    }

    ngOnInit(): void {
        if (this.tpTabs) {
            this.tpTabs = this.getTabsWithTitle(this.tpTabs);
            this.setActiveTabAsTpTabs(this.tpActiveTab as ITab);
            this.gtmTrackTabName();
        }
    }

    detectActiveTab(tab: ITab): void {
        if (this.detectGTMLocation && tab) {
            this.gtmTrackingService.setLocation((tab.title as string) || (tab.label as string) || (tab.name as string), this.detectGTMLocation);
        }
    }

    ngOnChanges(changesObj: SimpleChanges): void {
        if (changesObj?.tpActiveTab?.currentValue) {
            const { currentValue, previousValue } = changesObj?.tpActiveTab;
            if (currentValue?.id !== previousValue?.id && currentValue?.label && (currentValue?.url || this.preBuilt)) {
                this.gtmTrackTabName();
            }
        }
        if (changesObj.tpTabs && !changesObj.tpTabs.firstChange) {
            this.tpTabs = this.getTabsWithTitle(this.tpTabs);
        }
        if (changesObj.tpActiveTab && !changesObj.tpActiveTab.firstChange) {
            this.setActiveTabAsTpTabs(this.tpActiveTab as ITab);
        }
    }

    ngAfterViewInit(): void {
        if (this.casinoMyBetsIntegratedService.isMyBetsInCasino) {
            this.casinoMyBetsIntegratedService.bmaInit();
            this.setActiveTabForCasino = this.casinoMyBetsIntegratedService.getOpenBetTabActiveStatus();
            if (!!this.setActiveTabForCasino) {
                this.tpTabs.forEach((myBetsTab: ITab) => {
                    if (myBetsTab.title === 'Open Bets') {
                        myBetsTab.selected = true;
                    }
                });
            }
        }
    }

    ngOnDestroy(): void {
        if (this.detectGTMLocation) {
            this.gtmTrackingService.clearLocation(this.detectGTMLocation);
        }
        /* For surfacebet reactions GA Track */
        this.storage.remove('activetab');
    }

    /**
     * Set Active Class
     * @param {ITab} tab
     * @returns {boolean}
     */
    setActiveClass(tab: ITab): boolean {
        return !!this.tpActiveTab ? !!(tab?.id === this.tpActiveTab?.id) : !!tab?.selected;
    }

    /**
     * Set tpActiveTab as tpTabs if only 1 tab is being displayed
     * @param {ITab} tpActiveTab
     */
    private setActiveTabAsTpTabs(tpActiveTab: ITab): void {
        if (this.tpTabs && this.tpTabs.length === 1 && this.tpActiveTab !== undefined) {
            this.tpTabs = [tpActiveTab];
        }
    }

    /**
     * Get tabs list with translated title property
     * @param {ITab[]} tabs
     * @returns ITab[]
     */
    private getTabsWithTitle(tabs: ITab[]): ITab[] {
        const slicedTabs = this.maxElementsToDisplay ? tabs.slice(0, this.maxElementsToDisplay) : tabs;
        slicedTabs.forEach((tab: ITab) => {
            const title: string = (tab.title as string) || (tab.label as string);
            tab.title = title && title.match(/\./) ? this.locale.getString(title) : title;
        });

        return slicedTabs;
    }

    private gtmTrackTabName(): void {
        const activeTab = _.find(this.tpTabs, (tab) => this.setActiveClass(tab));
        /* For surfacebet reactions GA Track */
        this.storage.set('activetab', activeTab);
        activeTab && activeTab.title && this.gtmTrackingService.sendGTMDataOnTabClick(activeTab, '', this.preBuilt);
        this.detectActiveTab(activeTab as ITab);
    }
}
